import { ContextualAttachment } from '@/features/ai-agents/shared/models';
import { truncate } from '@/utils/types';

export function extractHTMLAttachment(
  selector: string,
  runInIframe: (callback: (doc: Document, win: Window) => void) => void,
): ContextualAttachment {
  let payload;
  runInIframe((doc) => {
    const element = doc.querySelector(`${selector}`);
    payload = element?.innerHTML || ``;
  });

  return {
    id: selector,
    caption: `Content for ${truncate(selector, 25)}`,
    payload,
    type: `application/html`,
  };
}

export function extractCSSAttachment(
  selector: string,
  runInIframe: (callback: (doc: Document, win: Window) => void) => void,
): ContextualAttachment {
  const cssContent = extractCSSContent(selector, runInIframe);

  return {
    id: `${selector}-css`,
    caption: `Styles for ${truncate(selector, 25)}`,
    payload: cssContent,
    type: `text/css`,
  };
}

function extractCSSContent(
  selector: string,
  runInIframe: (callback: (doc: Document, win: Window) => void) => void,
): string {
  let cssContent = ``;

  runInIframe((doc) => {
    const rootElement = doc.querySelector(selector);
    if (!rootElement) return;

    // 1. Collect all unique class names used within the element subtree
    const usedClasses = new Set<string>();
    const elements = [
      rootElement,
      ...Array.from(rootElement.querySelectorAll(`*`)),
    ];

    elements.forEach((el) => {
      el.classList.forEach((cls) => usedClasses.add(cls));
    });

    // 2. Iterate over all stylesheets to find matching rules
    const styleSheets = Array.from(doc.styleSheets);
    styleSheets.forEach((sheet) => {
      try {
        // Accessing cssRules might fail for cross-origin stylesheets
        const rules = Array.from(sheet.cssRules || []);
        rules.forEach((rule) => {
          // Check if the selector text contains any of our used classes
          // This is a naive check; for strict correctness, one would parse the selector
          const { selectorText } = rule as CSSStyleRule;
          if (!selectorText) return;

          const involvesUsedClass = Array.from(usedClasses).some((cls) => {
            // Matches .classname at start, end, or surrounded by non-word chars
            const regex = new RegExp(`(^|[^\\w-])${cls}([^\\w-]|$)`);
            return regex.test(selectorText);
          });

          if (involvesUsedClass) {
            cssContent += `${rule.cssText}\n`;
          }
        });
      } catch (e) {
        console.warn(`Could not access stylesheet: ${sheet.href}`, e);
      }
    });
  });

  return cssContent;
}
